import type { Locale } from "@repo/fika-shared/src/locale"
import { createMemo, createSelector, For, Show } from "solid-js"
import { Box } from "@/designSystem/components/box"
import { Icon } from "@/designSystem/components/icon"
import { Spinner } from "@/designSystem/components/spinner"
import { Text } from "@/designSystem/components/text"
import { Tooltip } from "@/designSystem/components/tooltip"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getLanguageLabel } from "@/lib/i18n"
import {
  inlineLinkButtonClass,
  languageBannerClass,
  languageLinkClass,
  languageListClass,
  languageSwitcherRowClass,
  separatorClass,
  statusMessageClass,
  statusTextClass,
} from "./language-banner.css"

type LanguageOption = {
  locale: string
  isMain: boolean
  isActive: boolean
  isOutdated: boolean
}

type Props = {
  languages: LanguageOption[]
  onSwitchLocale: (locale: Locale) => void
  isActiveLocaleOutdated?: boolean
  onRegenerateTranslation?: () => void
  onMarkAsUpdated?: () => void
  isRegenerating?: boolean
}

export function LanguageBanner(props: Props) {
  const { LL } = useI18nContext()

  const mainLanguage = createMemo(() => props.languages.find(l => l.isMain))
  const alternativeLanguages = createMemo(() =>
    props.languages.filter(l => !l.isMain),
  )
  const activeLanguage = createMemo(() => props.languages.find(l => l.isActive))
  const activeLocale = createMemo(() => activeLanguage()?.locale)
  const isSelected = createSelector(activeLocale)
  const isViewingTranslation = createMemo(
    () => activeLanguage() && !activeLanguage()?.isMain,
  )

  return (
    <Box class={languageBannerClass}>
      <Box gap="12">
        <div class={languageSwitcherRowClass}>
          <Box flexDirection="row" alignItems="center" gap="8">
            <Icon name="languages" size="14" color="sand500" />
            <Text fontSize="s" color="sand600">
              {LL().post.alsoIn()}
            </Text>
            <Box class={languageListClass}>
              <Show when={mainLanguage()}>
                {main => (
                  <Box flexDirection="row" alignItems="center" gap="6">
                    <button
                      type="button"
                      class={languageLinkClass({
                        isActive: isSelected(main().locale),
                      })}
                      onClick={() =>
                        props.onSwitchLocale(main().locale as Locale)
                      }
                      disabled={props.isRegenerating}
                    >
                      {getLanguageLabel(main().locale as Locale)}
                    </button>
                    <Icon name="globe" size="14" color="sand600" />
                  </Box>
                )}
              </Show>
              <Show when={alternativeLanguages().length > 0}>
                <span class={separatorClass} />
              </Show>
              <For each={alternativeLanguages()}>
                {language => (
                  <Box flexDirection="row" alignItems="center" gap="6">
                    <button
                      type="button"
                      class={languageLinkClass({
                        isActive: isSelected(language.locale),
                      })}
                      onClick={() =>
                        props.onSwitchLocale(language.locale as Locale)
                      }
                      disabled={props.isRegenerating}
                    >
                      {getLanguageLabel(language.locale as Locale)}
                    </button>
                    <Show
                      when={language.isOutdated}
                      fallback={
                        <Icon name="check" size="14" color="emerald600" />
                      }
                    >
                      <Tooltip description={LL().post.translationOutdated()}>
                        <Icon
                          name="alert-triangle"
                          size="14"
                          color="amber600"
                        />
                      </Tooltip>
                    </Show>
                  </Box>
                )}
              </For>
            </Box>
          </Box>
        </div>
        <Show when={props.isActiveLocaleOutdated || props.isRegenerating}>
          <Show
            when={!props.isRegenerating}
            fallback={
              <div class={statusMessageClass({ status: "outdated" })}>
                <div class={statusTextClass}>
                  <Spinner size="small" />
                  <Text fontSize="s" color="sand800">
                    {LL().post.updatingTranslation()}
                  </Text>
                </div>
              </div>
            }
          >
            <div class={statusMessageClass({ status: "outdated" })}>
              <div class={statusTextClass}>
                <Icon name="alert-triangle" size="18" color="amber600" />
                <Text fontSize="s" color="sand800">
                  {LL().post.translationOutdatedMessage()}{" "}
                  <button
                    type="button"
                    class={inlineLinkButtonClass({ status: "outdated" })}
                    onClick={props.onRegenerateTranslation}
                  >
                    {LL().post.regenerateTranslation()}
                  </button>{" "}
                  {LL().post.translationOutdatedOr()}{" "}
                  <button
                    type="button"
                    class={inlineLinkButtonClass({ status: "outdated" })}
                    onClick={props.onMarkAsUpdated}
                  >
                    {LL().post.markTranslationAsUpdated()}
                  </button>
                  .
                </Text>
              </div>
            </div>
          </Show>
        </Show>
        <Show when={isViewingTranslation() && !props.isActiveLocaleOutdated}>
          <div class={statusMessageClass({ status: "upToDate" })}>
            <div class={statusTextClass}>
              <Icon name="check" size="18" color="emerald600" />
              <Text fontSize="s" color="sand800">
                {LL().post.translationUpToDate()}
              </Text>
            </div>
          </div>
        </Show>
      </Box>
    </Box>
  )
}
